import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { MARKET_PROMOTION_PERMISSIONS } from '@/constants/permissions';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
} from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { zonalManagersQueryOptions } from '@/features/users/api/users.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { getFilterPanelProps } from '@/hooks/useUrlSyncedFilters';
import { getDateFromToFilterFields, getZonalManagerFilterField } from '@/utils/filterFields';
import { donationBatchesInfiniteQueryOptions } from '../api/marketPromotionP2.api';
import { DonationBatchActionDialog } from '../components/DonationBatchActionDialog';
import { DONATION_BATCH_COLUMN_ID, getDonationBatchColumns } from '../components/MpP2Columns';
import { DONATION_BATCH_ORIGIN } from '../constants/marketPromotion.constants';
import { useDonationBatchAction } from '../hooks/useDonationBatchAction';
import { useDonationBatchFilters, type DonationBatchPanel } from '../hooks/useDonationBatchFilters';

interface DonationBatchPageProps {
  panel: DonationBatchPanel;
  title: string;
}

export function DonationBatchPage({ panel, title }: DonationBatchPageProps) {
  const navigate = useNavigate();
  const controller = useDonationBatchFilters(panel);
  const { search } = controller;

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const lastViewedBatchId = useRevealLastViewedRow('donationBatches');
  const { action, actingBatch, closeAction } = useDonationBatchAction(panel);

  const stage = panel === 'publish' ? 'sales' : 'accounts';
  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    donationBatchesInfiniteQueryOptions(stage, search),
  );
  const { data: zonalManagers } = useQuery(zonalManagersQueryOptions());

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={title}
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={search.dateFrom}
            dateTo={search.dateTo}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: 'Batch number, name or item',
        }}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
        createAction={
          panel === 'publish'
            ? {
                label: 'New Batch',
                mobileLabel: 'New Batch',
                onClick: () => void navigate({ to: '/market-promotion/publish-panel/new' }),
                requires: MARKET_PROMOTION_PERMISSIONS.PUBLISH_PANEL_CREATE,
              }
            : undefined
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AdvancedFilterPanel
          fields={[...getDateFromToFilterFields(), getZonalManagerFilterField(zonalManagers)]}
          {...getFilterPanelProps(controller)}
        />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the batches"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={getDonationBatchColumns(
            (batch) => (
              <Link
                to="/market-promotion/publish-panel/batches/$batchId"
                params={{ batchId: batch.batchId }}
                search={{ from: DONATION_BATCH_ORIGIN[panel] }}
              />
            ),
            action,
            search.search,
          )}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId={DONATION_BATCH_COLUMN_ID}
          isRowHighlighted={(batch) => batch.batchId === lastViewedBatchId}
          emptyMessage="No donation batch has ever been published"
          {...loadMoreProps}
        />
      )}

      <DonationBatchActionDialog panel={panel} batch={actingBatch} onClose={closeAction} />
    </div>
  );
}
